import type { Shema } from '../api/odjemalec';

export type Termin = Shema['Termin'];

/** Trajanja za izbiro v oknu termina (minute). */
export const TRAJANJA: { value: string; label: string }[] = [
  { value: '30', label: '30 min' },
  { value: '60', label: '1 ura' },
  { value: '90', label: '1,5 ure' },
  { value: '120', label: '2 uri' },
  { value: '180', label: '3 ure' },
  { value: '240', label: '4 ure' },
  { value: '480', label: 'Cel dan (8 ur)' },
];

/** Prva in zadnja ura v mreži koledarja (termini zunaj se pokažejo na robu). */
export const PRVA_URA = 7;
export const ZADNJA_URA = 18;

const DNEVI = ['nedelja', 'ponedeljek', 'torek', 'sreda', 'četrtek', 'petek', 'sobota'];
const dd = (n: number) => String(n).padStart(2, '0');

/** Datum v lokalnem času → »2026-11-04« (za API in URL). */
export function isoDan(d: Date): string {
  return `${d.getFullYear()}-${dd(d.getMonth() + 1)}-${dd(d.getDate())}`;
}

/** »2026-11-04« → lokalna polnoč. */
export function izIsoDne(iso: string): Date {
  const [l, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(l!, (m ?? 1) - 1, d ?? 1);
}

/** Ponedeljek tedna, v katerem je dan (»2026-11-04« → »2026-11-02«). */
export function zacetekTedna(iso: string): string {
  const d = izIsoDne(iso);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return isoDan(d);
}

/** Dan, zamaknjen za {@code dni} (lahko negativno). */
export function zamakni(iso: string, dni: number): string {
  const d = izIsoDne(iso);
  d.setDate(d.getDate() + dni);
  return isoDan(d);
}

/** Dnevi tedna od ponedeljka ({@code stevilo} = 6 brez nedelje, 7 z njo). */
export function dneviTedna(ponedeljek: string, stevilo: number): string[] {
  return Array.from({ length: stevilo }, (_, i) => zamakni(ponedeljek, i));
}

/** »2026-11-04« → »sreda, 4. 11.«; {@code kratko}: »sre 4. 11.«. */
export function imeDne(iso: string, kratko = false): string {
  const d = izIsoDne(iso);
  const ime = DNEVI[d.getDay()]!;
  return `${kratko ? ime.slice(0, 3) : ime}${kratko ? '' : ','} ${d.getDate()}. ${d.getMonth() + 1}.`;
}

/** Teden v glavi: »2.–8. 11. 2026« ali »30. 11.–6. 12. 2026«. */
export function obdobjeTedna(ponedeljek: string, stevilo: number): string {
  const a = izIsoDne(ponedeljek);
  const b = izIsoDne(zamakni(ponedeljek, stevilo - 1));
  const leto = b.getFullYear();
  if (a.getMonth() === b.getMonth()) {
    return `${a.getDate()}.–${b.getDate()}. ${b.getMonth() + 1}. ${leto}`;
  }
  return `${a.getDate()}. ${a.getMonth() + 1}.${a.getFullYear() !== leto ? ` ${a.getFullYear()}` : ''}–${b.getDate()}. ${b.getMonth() + 1}. ${leto}`;
}

/** Lokalni čas s strežnika (»2026-11-04T08:30:00«) → »8.30«. */
export function ura(cas: string | undefined): string {
  if (!cas) {
    return '';
  }
  const [h, m] = cas.slice(11, 16).split(':');
  return `${Number(h)}.${m}`;
}

/** Ure v dnevu kot decimalno število (»…T08:30« → 8,5) za položaj v mreži. */
export function urVDnevu(cas: string): number {
  const [h, m] = cas.slice(11, 16).split(':').map(Number);
  return (h ?? 0) + (m ?? 0) / 60;
}

/** Začetek za API iz dneva in ure »08:30« → »2026-11-04T08:30:00«. */
export function zacetekZaApi(dan: string, cas: string): string {
  return `${dan.slice(0, 10)}T${cas.slice(0, 5)}:00`;
}

/**
 * Stolpci za termine dneva, ki se prekrivajo: vsak termin dobi {@code stolpec} in {@code stolpcev} (širina v mreži
 * je 1/stolpcev). Termini so razvrščeni po začetku (kot jih vrne strežnik).
 */
export function razporedi<T extends { zacetek?: string; konec?: string }>(
  termini: T[],
): { t: T; stolpec: number; stolpcev: number }[] {
  const izid: { t: T; stolpec: number; stolpcev: number }[] = [];
  let skupina: { t: T; stolpec: number; stolpcev: number }[] = [];
  let konecSkupine = '';
  const zakljuci = () => {
    const n = Math.max(1, ...skupina.map((x) => x.stolpec + 1));
    skupina.forEach((x) => (x.stolpcev = n));
    izid.push(...skupina);
    skupina = [];
  };
  for (const t of termini) {
    const z = t.zacetek ?? '';
    if (skupina.length > 0 && z >= konecSkupine) {
      zakljuci();
      konecSkupine = '';
    }
    const zasedeni = new Set(skupina.filter((x) => (x.t.konec ?? '') > z).map((x) => x.stolpec));
    let stolpec = 0;
    while (zasedeni.has(stolpec)) {
      stolpec++;
    }
    skupina.push({ t, stolpec, stolpcev: 1 });
    if ((t.konec ?? '') > konecSkupine) {
      konecSkupine = t.konec ?? '';
    }
  }
  zakljuci();
  return izid;
}
